import { Directive, ElementRef, Injector, afterNextRender, inject, output, signal } from '@angular/core';
import type { ListboxValueChangeEvent } from '@angular/cdk/listbox';

/**
 * Open/close behaviour for a filter dropdown built from a trigger button
 * (`aria-haspopup="listbox"`) and a CDK listbox, used as a host directive.
 *
 * It closes on a click outside the host or on Escape, moves focus into the list when
 * opened and back to the trigger after a choice, and emits `opened` so siblings can close.
 */
@Directive({
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'close()',
  },
})
export class FilterDropdown {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly injector = inject(Injector);
  private readonly openState = signal(false);

  readonly isOpen = this.openState.asReadonly();
  readonly opened = output<void>();

  open(): void {
    if (this.isOpen()) return;
    this.openState.set(true);
    this.opened.emit();
    // Focus the list once it is rendered so the arrow keys and type-ahead work right away.
    afterNextRender(() => this.query('[role="listbox"]')?.focus(), { injector: this.injector });
  }

  close(): void {
    this.openState.set(false);
  }

  toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Writes the listbox selection to `target`, closes the menu and returns focus to the trigger.
   * Clicking the already-selected option deselects it in the listbox; `target` is kept then.
   */
  choose<T>({ value: [selected] }: ListboxValueChangeEvent<T>, target: { set(value: T): void }): void {
    if (selected !== undefined) target.set(selected);
    this.close();
    this.query('[aria-haspopup="listbox"]')?.focus();
  }

  protected onDocumentClick(event: MouseEvent): void {
    // composedPath() is captured at dispatch, so it still holds the host even if the
    // clicked element was re-rendered away before this listener ran.
    if (this.isOpen() && !event.composedPath().includes(this.host)) {
      this.close();
    }
  }

  private query(selector: string): HTMLElement | null {
    return this.host.querySelector<HTMLElement>(selector);
  }
}
